import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ClipboardText, Plus, DownloadSimple, Trash, MagnifyingGlass, CheckCircle, XCircle, Clock, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import useAuthStore from '../store/authStore';
import MemoModal from '../components/MemoModal';
import { confirm } from '../components/ConfirmDialog';

import { notify } from '../lib/notify';
import { InlineSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLASSIFICATION_COLORS = {
  // New 5-level scheme
  'Level 0 – Process Failure (No Personal Memo)': '#10B981',
  'Level 1 – Honest Mistake / Slip': '#3B82F6',
  'Level 2 – At-Risk Behaviour': '#F59E0B',
  'Level 3 – Recklessness / Negligence': '#EF4444',
  'Level 4 – Wilful Violation / Misconduct': '#991B1B',
  // Legacy 4-level scheme (kept for old memos)
  'Level 1 – Process Failure': '#10B981',
  'Level 2 – Human Error': '#3B82F6',
  'Level 3 – Negligence': '#EF4444',
  'Level 4 – Willful Violation': '#991B1B',
};

const ACK_STATUS = {
  pending:      { color: '#F59E0B', bg: '#FEF3C7', label: 'Pending', icon: Clock },
  acknowledged: { color: '#10B981', bg: '#D1FAE5', label: 'Acknowledged', icon: CheckCircle },
  disputed:     { color: '#EF4444', bg: '#FEE2E2', label: 'Disputed', icon: XCircle },
};

const Memos = () => {
  const { user } = useAuthStore();
  const canIssue = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const canDelete = ['Vice President', 'President'].includes(user?.role);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMemo, setEditingMemo] = useState(null);
  const [viewMemo, setViewMemo] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (classFilter !== 'all') params.classification = classFilter;
      const [list, statsResp] = await Promise.all([
        axios.get(`${API}/memos`, { params }),
        canIssue ? axios.get(`${API}/memos/stats`) : Promise.resolve({ data: null }),
      ]);
      setItems(list.data.items || []);
      setStats(statsResp.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, statusFilter, classFilter, canIssue]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const downloadPDF = (memo) => {
    const token = localStorage.getItem('token');
    if (!token) { notify('Please log in again'); return; }
    const link = document.createElement('a');
    link.href = `${API}/memos/${memo.id}/pdf?token=${encodeURIComponent(token)}`;
    link.setAttribute('download', `${memo.memo_id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDelete = async (memo) => {
    const ok = await confirm({
      title: `Delete memo ${memo.memo_id}?`,
      message: 'This memo will be permanently deleted. This cannot be undone.',
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try { await axios.delete(`${API}/memos/${memo.id}`); fetchAll(); }
    catch (err) { notify(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="memos-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 lg:mb-8">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="memos-title">
            Accountability Memos
          </h1>
          <p className="text-[#6D8E87]">{canIssue ? 'Issue, track and manage employee memos' : 'Memos issued to you'}</p>
        </div>
        {canIssue && (
          <button onClick={() => { setEditingMemo(null); setShowModal(true); }}
            className="flex items-center gap-2 px-5 py-3 bg-[#00a69c] text-white rounded-full font-medium hover:bg-[#00bfb3] transition"
            data-testid="create-memo-btn">
            <Plus size={20} weight="bold" /> New Memo
          </button>
        )}
      </div>

      {canIssue && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="medical-card p-4"><div className="text-xs uppercase text-[#6D8E87] mb-1">Total</div><div className="text-2xl font-bold text-[#052A23]" style={{fontFamily:'Outfit'}}>{stats.total}</div></div>
          <div className="medical-card p-4"><div className="text-xs uppercase text-[#6D8E87] mb-1">Acknowledged</div><div className="text-2xl font-bold text-emerald-600" style={{fontFamily:'Outfit'}}>{stats.acknowledged}</div></div>
          <div className="medical-card p-4"><div className="text-xs uppercase text-[#6D8E87] mb-1">Pending</div><div className="text-2xl font-bold text-amber-600" style={{fontFamily:'Outfit'}}>{stats.pending}</div></div>
          <div className="medical-card p-4"><div className="text-xs uppercase text-[#6D8E87] mb-1">Disputed</div><div className="text-2xl font-bold text-red-500" style={{fontFamily:'Outfit'}}>{stats.disputed}</div></div>
          <div className="medical-card p-4"><div className="text-xs uppercase text-[#6D8E87] mb-1">Total Penalty</div><div className="text-2xl font-bold text-[#00a69c]" style={{fontFamily:'Outfit'}}>₹{(stats.total_penalty_inr || 0).toLocaleString('en-IN')}</div></div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D8E87]" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search memo, employee, code..."
            className="pl-9 pr-3 py-2 text-sm border border-[#D5E8E4] rounded-lg w-64 bg-white" data-testid="memo-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-[#D5E8E4] rounded-lg bg-white" data-testid="memo-status-filter">
          <option value="all">All Status</option>
          <option value="issued">Issued</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="disputed">Disputed</option>
        </select>
        <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-[#D5E8E4] rounded-lg bg-white" data-testid="memo-class-filter">
          <option value="all">All Classifications</option>
          <option value="Level 0 – Process Failure (No Personal Memo)">Level 0 – Process Failure</option>
          <option value="Level 1 – Honest Mistake / Slip">Level 1 – Honest Mistake / Slip</option>
          <option value="Level 2 – At-Risk Behaviour">Level 2 – At-Risk Behaviour</option>
          <option value="Level 3 – Recklessness / Negligence">Level 3 – Recklessness / Negligence</option>
          <option value="Level 4 – Wilful Violation / Misconduct">Level 4 – Wilful Violation / Misconduct</option>
        </select>
      </div>

      {loading ? (
        <InlineSkeleton />
      ) : items.length === 0 ? (
        <div className="medical-card p-12 text-center" data-testid="no-memos">
          <ClipboardText size={48} className="mx-auto text-[#D5E8E4] mb-3" />
          <p className="text-[#6D8E87] text-lg">No memos to show.</p>
        </div>
      ) : (
        <div className="medical-card overflow-x-auto" data-testid="memos-table-wrap">
          <table className="w-full min-w-[900px]">
            <thead className="bg-[#F4F9F7] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3 text-[#3B5A54]">Memo ID</th>
                <th className="text-left p-3 text-[#3B5A54]">Employee</th>
                <th className="text-left p-3 text-[#3B5A54]">Date</th>
                <th className="text-left p-3 text-[#3B5A54]">Classification</th>
                <th className="text-left p-3 text-[#3B5A54]">Penalty</th>
                <th className="text-left p-3 text-[#3B5A54]">Acknowledged</th>
                <th className="text-right p-3 text-[#3B5A54]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(m => {
                const ack = ACK_STATUS[m.employee_acknowledgement_status] || ACK_STATUS.pending;
                const AckIcon = ack.icon;
                const cColor = CLASSIFICATION_COLORS[m.incident_classification] || '#6D8E87';
                return (
                  <tr key={m.id} className="border-t border-[#F4F9F7] hover:bg-[#FAFFFE]" data-testid={`memo-row-${m.id}`}>
                    <td className="p-3 font-mono text-sm text-[#052A23]">{m.memo_id}</td>
                    <td className="p-3"><div className="font-medium text-[#052A23]">{m.employee_name}</div><div className="text-xs text-[#6D8E87]">{m.employee_code} · {m.designation}</div></td>
                    <td className="p-3 text-sm text-[#3B5A54]">{m.date_of_incident}</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ backgroundColor: `${cColor}20`, color: cColor }}>{m.incident_classification}</span></td>
                    <td className="p-3 text-sm">{(m.penalty_amount || 0) > 0 ? <span className="font-semibold text-[#052A23]">₹{m.penalty_amount.toLocaleString('en-IN')}</span> : <span className="text-[#6D8E87]">—</span>}</td>
                    <td className="p-3"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: ack.bg, color: ack.color }}><AckIcon size={12} weight="bold" />{ack.label}</span></td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => setViewMemo(m)} className="px-2 py-1 text-xs text-[#00a69c] hover:bg-[#E2F3F0] rounded" data-testid={`view-memo-${m.id}`}>View</button>
                        <button onClick={() => downloadPDF(m)} className="px-2 py-1 text-xs text-[#00a69c] hover:bg-[#E2F3F0] rounded inline-flex items-center gap-1" data-testid={`pdf-memo-${m.id}`}><DownloadSimple size={12} weight="bold"/> PDF</button>
                        {canDelete && <button onClick={() => handleDelete(m)} className="px-2 py-1 text-xs text-red-500 hover:bg-red-50 rounded inline-flex items-center gap-1" data-testid={`del-memo-${m.id}`}><Trash size={12} weight="bold"/></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <MemoModal open={showModal} onClose={() => { setShowModal(false); setEditingMemo(null); }} existing={editingMemo} onSaved={fetchAll} />
      )}

      {viewMemo && (
        <MemoView memo={viewMemo} onClose={() => setViewMemo(null)} canDownload onDownload={() => downloadPDF(viewMemo)} onAcknowledged={fetchAll} />
      )}
    </div>
  );
};

// Read-only view + employee acknowledgement
const MemoView = ({ memo, onClose, onDownload, onAcknowledged }) => {
  const { user } = useAuthStore();
  const isEmployee = user && memo.employee_user_id === user.id;
  const pending = memo.employee_acknowledgement_status === 'pending';
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const act = async (action) => {
    if (!notes && action === 'dispute') { notify('Please add notes when disputing'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/memos/${memo.id}/acknowledge`, { action, notes });
      onAcknowledged?.();
      onClose();
    } catch (err) { notify(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const F = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-[#F4F9F7] text-sm">
      <div className="font-medium text-[#3B5A54]">{label}</div>
      <div className="col-span-2 text-[#052A23] whitespace-pre-wrap">{value || '—'}</div>
    </div>
  );
  const H = ({ children }) => <h3 className="text-sm font-bold text-white bg-[#00a69c] px-3 py-1.5 rounded mt-5 mb-2">{children}</h3>;

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="memo-view-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
            {memo.memo_id} — {memo.employee_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-1">
          <H>Section 1 — Incident Information</H>
          <F label="Date" value={memo.date_of_incident} /><F label="Time" value={memo.time_of_incident} />
          <F label="Plant/Location" value={memo.plant_location === 'Other' ? `Other (${memo.plant_location_other})` : memo.plant_location} />
          <F label="Department" value={memo.department} /><F label="Shift" value={memo.shift} /><F label="Reported By" value={memo.reported_by} />
          <H>Section 2 — Employee Details</H>
          <F label="Name" value={memo.employee_name} /><F label="Code" value={memo.employee_code} /><F label="Designation" value={memo.designation} /><F label="Reporting Manager" value={memo.reporting_manager} />
          <H>Section 3 — Incident Description</H>
          <F label="Description" value={memo.incident_description} /><F label="Exact Location" value={memo.exact_location_of_incident} /><F label="Repeat Issue?" value={memo.repeat_issue} />
          <H>Section 4 — Impact Assessment</H>
          <F label="Production Loss" value={memo.production_loss_units} /><F label="Financial Impact" value={memo.estimated_financial_impact ? `₹${memo.estimated_financial_impact.toLocaleString('en-IN')}` : '—'} />
          <F label="Quality Impact" value={memo.quality_impact} /><F label="Safety Impact" value={memo.safety_impact} /><F label="Delivery Delay" value={memo.delivery_delay} />
          <H>Section 5 — RCA</H>
          <F label="Method" value={memo.rca_method} /><F label="Category" value={memo.root_cause_category} /><F label="Statement" value={memo.root_cause_statement} />
          {memo.rca_checklist && (() => {
            const rc = memo.rca_checklist || {};
            const disp = v => v === 'Yes' || v === 'No' ? v : (v === true ? 'Yes' : (v === false ? 'No' : (v || '—')));
            const s1 = rc.step1_incident_clarity || {}, s2 = rc.step2_system_check || {}, s5 = rc.step5_responsibility || {}, s6 = rc.step6_fairness || {};
            return (
              <div className="mt-3 p-3 bg-[#F4F9F7] rounded-lg text-xs space-y-1.5">
                <div className="font-bold text-[#052A23] text-sm mb-1">RCA Checklist</div>
                <div><span className="font-semibold text-[#3B5A54]">Step 1 — Incident Clarity:</span></div>
                <div className="ml-3">Time: <span className="font-medium">{s1.incident_time || '—'}</span> · Location: <span className="font-medium">{s1.incident_location || '—'}</span></div>
                {s1.incident_sequence && <div className="ml-3">Sequence: <span className="font-medium">{s1.incident_sequence}</span></div>}
                <div className="ml-3">Logs available: <span className="font-medium">{disp(s1.logs_available)}</span>{s1.logs_notes ? <> · Notes: <span className="font-medium">{s1.logs_notes}</span></> : null}</div>
                <div className="ml-3">Repeat issue: <span className="font-medium">{disp(s1.repeat_issue_confirmed)}</span></div>
                <div><span className="font-semibold text-[#3B5A54]">Step 2 — System Check:</span> SOP {disp(s2.sop_exists)} · Clear {disp(s2.sop_clear)} · Trained {disp(s2.employee_trained)} · Checklist {disp(s2.checklist_available)} · Supervision {disp(s2.supervision_present)}</div>
                {(rc.five_whys || []).filter(Boolean).length > 0 && (
                  <div>
                    <div className="font-semibold text-[#3B5A54]">5 Whys:</div>
                    {(rc.five_whys || []).map((w, i) => w && <div key={i} className="ml-3">#{i+1}: {w}</div>)}
                  </div>
                )}
                <div><span className="font-semibold text-[#3B5A54]">Step 5 — Responsibility:</span> Operator {disp(s5.operator_could_prevent)} · Supervisor {disp(s5.supervisor_could_prevent)} · System {disp(s5.system_could_prevent)} · Assigned: <span className="font-medium">{s5.assigned_responsibility_level || '—'}</span></div>
                <div><span className="font-semibold text-[#3B5A54]">Step 6 — Fairness:</span> Blamed for system? {disp(s6.being_blamed_for_system_failure)} · Anyone else also fail? {disp(s6.another_person_would_also_fail)}</div>
              </div>
            );
          })()}
          <H>Section 6 — Accountability</H>
          <F label="Accountable Person" value={memo.accountable_person} /><F label="Reason" value={memo.reason_for_accountability} />
          <H>Section 7 — Actions</H>
          <F label="Immediate Action" value={memo.immediate_corrective_action} />
          <F label="Immediate Action Owner" value={memo.immediate_action_owner} />
          <F label="Immediate Action Date" value={memo.immediate_action_date} />
          <F label="Preventive Action" value={memo.preventive_action} /><F label="Owner" value={memo.preventive_action_owner} /><F label="Target Date" value={memo.target_closure_date} />
          <H>Section 8 — Classification</H>
          <F label="Classification" value={memo.incident_classification} />
          <H>Section 9 — Action Taken</H>
          <F label="Action Types" value={(memo.action_types || []).join(', ')} />
          <F label="Penalty" value={memo.penalty_amount > 0 ? `₹${memo.penalty_amount.toLocaleString('en-IN')}` : '—'} />
          <F label="Remarks" value={memo.additional_remarks} />
          {memo.action_details && (memo.action_types || []).map(action => {
            const d = (memo.action_details || {})[action];
            if (!d || Object.values(d).every(v => !v)) return null;
            return (
              <div key={action} className="mt-2 p-3 bg-[#F4F9F7] border-l-4 border-[#00a69c] rounded-r-lg">
                <div className="text-xs font-bold text-[#00a69c] mb-1">{action} — Details</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(d).filter(([_, v]) => v).map(([k, v]) => (
                    <div key={k}><span className="text-[#6D8E87]">{k.replace(/_/g, ' ')}:</span> <span className="text-[#052A23] font-medium">{v}</span></div>
                  ))}
                </div>
              </div>
            );
          })}
          <H>Section 10 — Approvals</H>
          <F label="Supervisor" value={memo.supervisor_name} /><F label="Plant Head" value={memo.plant_head_approval} /><F label="HR" value={memo.hr_representative} />
          <H>Section 11 — Acknowledgement</H>
          <F label="Status" value={memo.employee_acknowledgement_status.toUpperCase()} />
          {memo.employee_acknowledged_at && <F label="At" value={memo.employee_acknowledged_at.slice(0,19).replace('T',' ')} />}
          {memo.employee_acknowledgement_notes && <F label="Notes" value={memo.employee_acknowledgement_notes} />}

          {/* Acknowledgement action for employee */}
          {isEmployee && pending && (
            <div className="p-4 mt-4 bg-amber-50 border-2 border-amber-300 rounded-xl" data-testid="ack-section">
              <div className="flex items-start gap-2 mb-3">
                <Warning size={20} weight="fill" className="text-amber-500 shrink-0 mt-0.5"/>
                <p className="text-sm text-amber-900">By acknowledging, you confirm that this memo has been read and explained to you. You may also dispute if you disagree, providing reasons in the notes.</p>
              </div>
              <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Optional notes (required if disputing)"
                className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm mb-3" data-testid="ack-notes"/>
              <div className="flex gap-2">
                <button onClick={() => act('dispute')} disabled={submitting} className="flex-1 px-4 py-2 border-2 border-red-400 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-50 disabled:opacity-50" data-testid="dispute-btn">Dispute</button>
                <button onClick={() => act('acknowledge')} disabled={submitting} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="acknowledge-btn">Acknowledge</button>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button onClick={onClose} className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50">Close</button>
            <button onClick={onDownload} className="flex-1 py-3 bg-[#00a69c] text-white rounded-lg font-semibold hover:bg-[#00bfb3] flex items-center justify-center gap-2" data-testid="download-pdf-btn">
              <DownloadSimple size={18} weight="bold"/> Download PDF
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Memos;
